import { useState, useEffect, useMemo } from "react";
import { Receipt } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { convertCurrency, formatCurrency, CURRENCY_SYMBOLS } from "@/lib/currencyConversion";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion } from "framer-motion";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";

interface Expense {
  id: string;
  amount: number;
  merchant: string;
  category: string;
  expense_date: string;
  currency: string;
}

interface MonthSpendingSummaryProps {
  userId: string;
  refreshTrigger?: number;
}

export const MonthSpendingSummary = ({ userId, refreshTrigger }: MonthSpendingSummaryProps) => {
  const { t } = useLanguage();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [previousMonthTotal, setPreviousMonthTotal] = useState<number | null>(null);
  const [userCurrency, setUserCurrency] = useState("EUR");
  const [isLoading, setIsLoading] = useState(true);

  // Stable month reference — only recalculate when month actually changes
  const currentMonth = useMemo(() => new Date(), []);

  useEffect(() => {
    const loadData = async () => {
      if (!userId) return;
      
      setIsLoading(true);
      try {
        // Get user currency
        const { data: settings } = await supabase
          .from("budget_settings")
          .select("currency")
          .eq("user_id", userId)
          .maybeSingle();
        
        const currency = settings?.currency || 'EUR';
        setUserCurrency(currency);

        // Get current month's expenses
        const monthStart = format(startOfMonth(currentMonth), "yyyy-MM-dd");
        const monthEnd = format(endOfMonth(currentMonth), "yyyy-MM-dd");

        const { data: expenseData, error } = await supabase
          .from("expenses")
          .select("id, amount, merchant, category, expense_date, currency")
          .eq("user_id", userId)
          .gte("expense_date", monthStart)
          .lte("expense_date", monthEnd)
          .order("expense_date", { ascending: false });

        if (error) {
          console.error("Error loading expenses:", error);
          return;
        }

        setExpenses(expenseData || []);

        // Get previous month total for trend
        const prevMonth = subMonths(currentMonth, 1);
        const prevMonthStart = format(startOfMonth(prevMonth), "yyyy-MM-dd");
        const prevMonthEnd = format(endOfMonth(prevMonth), "yyyy-MM-dd");

        const { data: prevExpenses } = await supabase
          .from("expenses")
          .select("amount, currency")
          .eq("user_id", userId)
          .gte("expense_date", prevMonthStart)
          .lte("expense_date", prevMonthEnd);

        if (prevExpenses && prevExpenses.length > 0) {
          const prevTotal = prevExpenses.reduce((sum, e) => {
            return sum + convertCurrency(Number(e.amount), e.currency || 'EUR', currency);
          }, 0);
          setPreviousMonthTotal(prevTotal);
        }
      } catch (error) {
        console.error("Error loading month summary:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [userId, refreshTrigger]);

  const summary = useMemo(() => {
    const total = expenses.reduce((sum, e) => {
      return sum + convertCurrency(Number(e.amount), e.currency || 'EUR', userCurrency);
    }, 0);

    const count = expenses.length;

    // Calculate change from previous month
    let changePercent: number | null = null;
    if (previousMonthTotal !== null && previousMonthTotal > 0) {
      changePercent = ((total - previousMonthTotal) / previousMonthTotal) * 100;
    }

    return { total, count, changePercent };
  }, [expenses, previousMonthTotal, userCurrency]);

  const formatMonth = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long'
    });
  };

  // Only show trend badge if change is significant (>5%)
  const showTrendBadge = summary.changePercent !== null && Math.abs(summary.changePercent) > 5;
  const trendIsDown = summary.changePercent !== null && summary.changePercent < 0;
  const absChange = summary.changePercent !== null ? Math.abs(summary.changePercent).toFixed(0) : "0";

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-4 bg-muted rounded w-1/3" />
        <div className="h-10 bg-muted rounded w-2/3 mx-auto" />
        <div className="h-4 bg-muted rounded w-1/4 mx-auto" />
      </div>
    );
  }

  if (expenses.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-6"
      >
        <Receipt className="w-10 h-10 mx-auto text-muted-foreground/50 mb-2" />
        <p className="text-muted-foreground text-sm">No expenses in {formatMonth(currentMonth)}</p>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center"
    >
      {/* Header row: month + compact trend badge */}
      <div className="flex items-center justify-center gap-2 mb-2">
        <p className="text-label-card">{formatMonth(currentMonth)} Spending</p>
        {showTrendBadge && (
          <span 
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              trendIsDown 
                ? "bg-success/15 text-success" 
                : "bg-destructive/15 text-destructive"
            }`}
          >
            {trendIsDown ? "↓" : "↑"} {absChange}%
          </span>
        )}
      </div>
      
      {/* THE ONE NUMBER - centered and prominent */}
      <p className="text-amount-large mb-2">
        {formatCurrency(summary.total, userCurrency)}
      </p>
      
      {/* Single meta line */}
      <p className="text-meta">
        {summary.count} expense{summary.count !== 1 ? 's' : ''}
      </p>
    </motion.div>
  );
};